import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Modal } from '../ds/index'

/**
 * Taking a payment. The server answers with either the address of Stripe's own
 * page (go there) or what Stripe's form needs to open inside the portal: the
 * session's secret, the publishable key, and the company's Stripe account the
 * payment runs on. Once paid, Stripe takes the page to the same return address
 * Stripe's own page would, so what happens after is the same either way.
 */
export type CheckoutStart = { url: string } | { client_secret: string; publishable_key: string; account: string }

// Stripe.js must come from Stripe (it handles the card number); the CSP allows exactly this origin.
const STRIPE_JS = 'https://js.stripe.com/endive/stripe.js'

type EmbeddedPage = { mount: (el: HTMLElement) => void; destroy: () => void }
type StripeJs = { createEmbeddedCheckoutPage: (o: { fetchClientSecret: () => Promise<string> }) => Promise<EmbeddedPage> | EmbeddedPage }
declare global { interface Window { Stripe?: (key: string, opts?: { stripeAccount?: string }) => StripeJs } }

let loading: Promise<void> | null = null
function loadStripe(): Promise<void> {
  if (window.Stripe) return Promise.resolve()
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = STRIPE_JS
      s.async = true
      s.onload = () => resolve()
      s.onerror = () => { loading = null; reject(new Error('Could not reach Stripe. Check your connection and try again.')) }
      document.head.appendChild(s)
    })
  }
  return loading
}

function StripeForm({ start, title, onClose }: { start: Extract<CheckoutStart, { client_secret: string }>; title: string; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let page: EmbeddedPage | null = null
    let gone = false
    ;(async () => {
      try {
        await loadStripe()
        const stripe = window.Stripe!(start.publishable_key, { stripeAccount: start.account })
        const p = await stripe.createEmbeddedCheckoutPage({ fetchClientSecret: async () => start.client_secret })
        if (gone) { p.destroy(); return }
        page = p
        if (box.current) p.mount(box.current)
        setReady(true)
      } catch (e) { if (!gone) setError((e as Error).message || 'The payment form did not open') }
    })()
    return () => { gone = true; page?.destroy() }
  }, [start])
  return (
    <Modal title={title} onClose={onClose}>
      {error ? <p style={{ color: 'var(--neg)' }}>{error}</p> : !ready && <p className="muted">Opening Stripe's secure form</p>}
      <div ref={box} className="stripe-embed" />
      <p className="muted" style={{ fontSize: 12, margin: 'var(--s-4) 0 var(--s-6)' }}>Card details go straight to Stripe. They are never stored here.</p>
    </Modal>
  )
}

/**
 * const pay = useCheckout('Add money')
 * pay.open(await post<CheckoutStart>(...), () => setBusy(false))
 * ...and render {pay.element}
 */
export function useCheckout(title: string) {
  const [open, setOpen] = useState<{ start: Extract<CheckoutStart, { client_secret: string }>; onClose?: () => void } | null>(null)
  const start = useCallback((r: CheckoutStart, onClose?: () => void) => {
    if ('url' in r) { window.location.href = r.url; return }
    setOpen({ start: r, onClose })
  }, [])
  const close = useCallback(() => { setOpen((o) => { o?.onClose?.(); return null }) }, [])
  const element: ReactNode = open ? <StripeForm start={open.start} title={title} onClose={close} /> : null
  return { open: start, element }
}
